import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import GlobalLoader from "../components/ui/GlobalLoader";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarProvider,
  SidebarTrigger,
  SidebarInset,
} from "../components/ui/sidebar";
import {
  Bell,
  Package,
  Truck,
  CheckCircle,
  BarChart3,
  Settings,
  LogOut,
  User,
  Building2,
  Users,
  Shield,
  Activity,
  Database,
  MapPin,
  Route,
  QrCode,
  Navigation,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  CustomDropdown,
  DropdownItem,
  DropdownSeparator,
  DropdownContent,
} from "../components/ui/custom-dropdown";
import { SocketDiagnostics } from "../components/SocketDiagnostics";
import { useAppSelector } from "@/redux/hooks";
import { useDispatch } from "react-redux";
import { useAppDispatch } from "@/redux/hooks";
import { logoutUser } from "@/redux/slices/auth/authSlice";
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead, Notification as ApiNotification } from "@/api/notificationService";

type DashboardPage =
  | "dashboard"
  | "orders"
  | "pickup"
  | "delivery"
  | "pharmacies"
  | "drivers"
  | "order-processing"
  | "route-management"
  | "assign-routes"
  | "barcodes"
  | "tracking"
  | "route-tracking"
  | "analytics"
  | "system"
  | "settings";

// Super Admin Menu Items
const superAdminMenuItems = [
  {
    id: "dashboard" as DashboardPage,
    title: "Dashboard",
    icon: BarChart3,
    description: "Platform analytics and KPIs",
    path: "/admin/dashboard",
  },
  {
    id: "pharmacies" as DashboardPage,
    title: "Pharmacy Network",
    icon: Building2,
    description: "Manage pharmacy partners",
    path: "/admin/pharmacies",
  },
  {
    id: "drivers" as DashboardPage,
    title: "Driver Fleet",
    icon: Users,
    description: "Driver management",
    path: "/admin/drivers",
  },
  {
    id: "order-processing" as DashboardPage,
    title: "Order Processing",
    icon: Package,
    description: "Process all orders",
    path: "/admin/order-processing",
  },
  {
    id: "route-management" as DashboardPage,
    title: "Route Management",
    icon: MapPin,
    description: "Create and manage delivery routes",
    path: "/admin/route-management",
  },
  {
    id: "assign-routes" as DashboardPage,
    title: "Assign Routes",
    icon: Route,
    description: "Assign routes to drivers",
    path: "/admin/assign-routes",
  },
  {
    id: "pickup" as DashboardPage,
    title: "Pickup Orders",
    icon: Truck,
    description: "Manage pickup requests",
    path: "/admin/pickup-orders",
  },
  // {
  //   id: "tracking" as DashboardPage,
  //   title: "Live Tracking",
  //   icon: Navigation,
  //   description: "Track orders and drivers in real-time",
  //   path: "/admin/tracking",
  // },
  {
    id: "route-tracking" as DashboardPage,
    title: "Route Tracking",
    icon: Route,
    description: "Track routes and drivers in real-time",
    path: "/admin/route-tracking",
  },
  // {
  //   id: "barcodes" as DashboardPage,
  //   title: "Barcode Management",
  //   icon: QrCode,
  //   description: "Generate and print barcodes",
  //   path: "/admin/barcodes",
  // },
  // {
  //   id: "analytics" as DashboardPage,
  //   title: "Advanced Analytics",
  //   icon: Activity,
  //   description: "Business intelligence",
  //   path: "/admin/analytics",
  // },
  
  // {
  //   id: "system" as DashboardPage,
  //   title: "System Management",
  //   icon: Database,
  //   description: "Platform configuration",
  //   path: "/admin/system",
  //   },
  {
    id: "settings" as DashboardPage,
    title: "Settings",
    icon: Settings,
    description: "Account and preferences",
    path: "/admin/settings",
  },
];

// Pharmacy Menu Items
const pharmacyMenuItems = [
  {
    id: "dashboard" as DashboardPage,
    title: "Dashboard",
    icon: BarChart3,
    description: "Pharmacy overview",
    path: "/pharmacy/dashboard",
  },
  {
    id: "orders" as DashboardPage,
    title: "Order Management",
    icon: Package,
    description: "Create and manage orders",
    path: "/pharmacy/orders",
  },
  {
    id: "pickup" as DashboardPage,
    title: "Pickup Requests",
    icon: Truck,
    description: "Schedule pickups",
    path: "/pharmacy/pickup",
  },
  // {
  //   id: "tracking" as DashboardPage,
  //   title: "Live Tracking",
  //   icon: Navigation,
  //   description: "Track your orders and drivers in real-time",
  //   path: "/pharmacy/tracking",
  // },
  {
    id: "route-tracking" as DashboardPage,
    title: "Route Tracking",
    icon: Route,
    description: "Track your routes and drivers in real-time",
    path: "/pharmacy/route-tracking",
  },
  // {
  //   id: "delivery" as DashboardPage,
  //   title: "Delivery Tracking",
  //   icon: CheckCircle,
  //   description: "Track deliveries",
  //   path: "/pharmacy/delivery",
  // },
  // {
  //   id: "analytics" as DashboardPage,
  //   title: "Analytics",
  //   icon: Activity,
  //   description: "Performance insights",
  //   path: "/pharmacy/analytics",
  // },
  {
    id: "barcodes" as DashboardPage,
    title: "QR Code Management",
    icon: QrCode,
    description: "Generate and print QR codes",
    path: "/pharmacy/barcodes",
  },
  {
    id: "settings" as DashboardPage,
    title: "Settings",
    icon: Settings,
    description: "Account and preferences",
    path: "/pharmacy/settings",
  },
];

export function DashboardLayout() {
  // const { user, setUser, isSuperAdmin } = useUser();

  const user = useAppSelector((state) => state.auth.user);
  const isSuperAdmin = user.isAdmin;
  const notifications = user?.notifications;
  const notificationsCount = user?.notificationCount;

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Real-time notifications
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationList, setNotificationList] = useState<ApiNotification[]>([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);

  // Fetch notifications from API (loads existing + real-time updates via socket)
  const fetchNotifications = async () => {
    setIsLoadingNotifications(true);
    try {
      console.log('📥 Fetching notifications from API...');
      const result = await getNotifications();
      console.log('📥 API Response:', result);
      console.log('📥 Notifications count:', result.notifications?.length || 0);
      console.log('📥 Current notification list before merge:', notificationList.length);
      
      if (result.notifications && Array.isArray(result.notifications)) {
        // Merge with existing notifications to avoid duplicates
        setNotificationList(prev => {
          const existingIds = new Set(prev.map(n => n.id));
          const newNotifications = result.notifications.filter(n => !existingIds.has(n.id));
          const merged = [...newNotifications, ...prev];
          console.log(`✅ Merged notifications: ${newNotifications.length} new, ${prev.length} existing, ${merged.length} total`);
          return merged;
        });
      } else {
        console.warn('⚠️ No notifications array in response');
        // Don't clear existing notifications, just don't add new ones
      }
    } catch (error: any) {
      console.error('❌ Error fetching notifications:', error);
      console.error('❌ Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      
      // If API is not implemented (404), show sample notifications for testing
      if (error?.response?.status === 404) {
        console.log('📝 API not implemented, showing sample notifications for testing...');
        console.log('📝 These are sample notifications - real notifications will come via socket events');
        const sampleNotifications: ApiNotification[] = [
          {
            id: 'sample-1',
            type: 'order',
            title: 'Pickup Order Assigned',
            message: 'Pickup order PICKUP-1759862267246-5GC06VCRX has been assigned to driver Ali abc',
            read: false,
            timestamp: new Date().toISOString(),
            priority: 'medium',
            orderId: 'PICKUP-1759862267246-5GC06VCRX',
            driverId: 'driver-ali-abc'
          },
          {
            id: 'sample-2',
            type: 'order',
            title: 'Pickup Order Assigned',
            message: 'Pickup order PICKUP-1759864929748-IB6EFCJA5 has been assigned to driver Ali abc',
            read: false,
            timestamp: new Date(Date.now() - 60000).toISOString(), // 1 minute ago
            priority: 'medium',
            orderId: 'PICKUP-1759864929748-IB6EFCJA5',
            driverId: 'driver-ali-abc'
          }
        ];
        setNotificationList(sampleNotifications);
        setUnreadCount(sampleNotifications.length);
        console.log('✅ Sample notifications loaded for testing');
      } else {
        // Set empty array on other errors
        setNotificationList([]);
      }
      
      // Show user-friendly message
      if (error?.response?.status === 404) {
        console.log('ℹ️ Notification endpoint not found - backend may not be implemented yet');
      }
    } finally {
      setIsLoadingNotifications(false);
    }
  };

  // Load existing notifications on mount, then rely on real-time updates
  useEffect(() => {
    fetchNotifications();
  }, []);

  // Handle notification click
  const handleNotificationClick = async (notification: ApiNotification) => {
    if (!notification.read) {
      console.log('Marking notification as read:', notification.id);
      const success = await markNotificationAsRead(notification.id);
      
      if (success) {
        // Update local state
        setNotificationList(prev =>
          prev.map(n =>
            n.id === notification.id ? { ...n, read: true } : n
          )
        );
        
        // Decrement unread count
        setUnreadCount(prev => Math.max(0, prev - 1));
        
        // toast.success('Notification marked as read');
      }
    }

    // Navigate if there's associated data
    if (notification.orderId) {
      navigate(`/${isSuperAdmin ? 'admin' : 'pharmacy'}/orders/${notification.orderId}`);
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    console.log('Marking all notifications as read');
    const success = await markAllNotificationsAsRead();
    
    if (success) {
      setNotificationList(prev =>
        prev.map(n => ({ ...n, read: true }))
      );
      setUnreadCount(0);
      // toast.success('All notifications marked as read');
    }
  };

  useEffect(() => {
    console.log('DashboardLayout: Setting up socket listeners...');
    
    // Import socket service dynamically
    import('../services/socket').then(({ default: socketService }) => {
      console.log('DashboardLayout: Socket service imported, connected:', socketService.isConnected());
      
      // Wait a bit for socket to connect if it's not connected yet
      const setupListeners = () => {
        if (!socketService.isConnected()) {
          console.log('DashboardLayout: Socket not connected yet, retrying in 1 second...');
          setTimeout(setupListeners, 1000);
          return;
        }

        console.log('DashboardLayout: Socket connected! Setting up event listeners...');

        // Join role-specific room
        if (isSuperAdmin) {
          console.log('DashboardLayout: Joining admin room');
          socketService.joinRoom('admin');
        } else {
          console.log('DashboardLayout: Joining pharmacy room:', `pharmacy:${user.id}`);
          socketService.joinRoom(`pharmacy:${user.id}`);
        }

        // Subscribe to real-time notifications
        const handleNotification = (data: any) => {
          console.log('✅ Real-time notification received:', data);
          
          // Add to notification list
          const newNotification: ApiNotification = {
            id: data.id,
            type: data.type,
            title: data.title,
            message: data.message,
            read: false,
            timestamp: data.timestamp,
            priority: data.priority,
            data: data.data,
            sender: data.sender,
            orderId: data.orderId,
            driverId: data.driverId
          };
          
          setNotificationList(prev => {
            // Check if notification already exists to prevent duplicates
            const exists = prev.some(notif => 
              notif.id === newNotification.id || 
              (notif.orderId === newNotification.orderId && 
               notif.title === newNotification.title && 
               notif.message === newNotification.message &&
               Math.abs(new Date(notif.timestamp).getTime() - new Date(newNotification.timestamp).getTime()) < 5000) // Within 5 seconds
            );
            
            if (exists) {
              console.log('⚠️ Duplicate notification detected, skipping:', newNotification.id);
              return prev;
            }
            
            console.log('✅ Adding new real-time notification:', newNotification.id);
            return [newNotification, ...prev];
          });
          
          // Show toast based on priority
          const toastOptions: any = {
            description: data.message,
            duration: data.priority === 'urgent' ? 10000 : 5000,
          };
          
          switch (data.priority) {
            case 'urgent':
              // toast.error(data.title, toastOptions);
              break;
            case 'high':
              // toast.warning(data.title, toastOptions);
              break;
            default:
              // toast.info(data.title, toastOptions);
          }
          
          // Increment unread count
          setUnreadCount(prev => prev + 1);
        };

        // Subscribe to order status changes
        const handleOrderStatus = (data: any) => {
          console.log('✅ Order status update:', data);
          // toast.info(`Order #${data.orderNumber} - ${data.newStatus}`, {
          //   description: `Status updated ${data.updatedBy ? 'by ' + data.updatedBy : ''}`,
          // });
        };

        // Subscribe to delivery status updates
        const handleDeliveryStatus = (data: any) => {
          console.log('✅ Delivery status update:', data);
          
          const statusMessages: Record<string, string> = {
            'picked_up': '📦 Order picked up by driver',
            'in_transit': '🚚 Order is in transit',
            'delivered': '✅ Order delivered successfully',
            'failed': '❌ Delivery failed',
          };
          
          // toast.success(statusMessages[data.status] || 'Delivery status updated', {
          //   description: `Order #${data.orderId}`,
          // });
        };

        console.log('DashboardLayout: Registering event handlers...');
        socketService.onNotification(handleNotification);
        socketService.onOrderStatusUpdate(handleOrderStatus);
        socketService.onDeliveryStatusUpdate(handleDeliveryStatus);
        console.log('DashboardLayout: Event handlers registered successfully!');

        // Return cleanup function for this setup
        return () => {
          console.log('DashboardLayout: Cleaning up event listeners...');
          socketService.offNotification(handleNotification);
          socketService.offOrderStatusUpdate(handleOrderStatus);
          socketService.offDeliveryStatusUpdate(handleDeliveryStatus);
        };
      };

      // Start setup and get cleanup function
      const cleanupListeners = setupListeners();

      // Main cleanup function
      return () => {
        console.log('DashboardLayout: Cleaning up socket listeners...');
        
        // Clean up event listeners
        if (cleanupListeners) {
          cleanupListeners();
        }
        
        // Leave room
        if (socketService.isConnected()) {
          socketService.leaveRoom(isSuperAdmin ? 'admin' : `pharmacy:${user.id}`);
        }
      };
    });
  }, [isSuperAdmin, user.id]);

  // const [notifications] = useState(isSuperAdmin ? 8 : 3);

  const handleLogout = () => {
    dispatch(logoutUser({}));
    navigate("/");
  };

  const handleProfileSettingsClick = () => {
    console.log("Profile settings clicked");
    const settingsPath = isSuperAdmin
      ? "/admin/settings"
      : "/pharmacy/settings";
    navigate(settingsPath);
  };

  const getMenuItems = () => {
    return isSuperAdmin ? superAdminMenuItems : pharmacyMenuItems;
  };

  const getPageTitle = () => {
    const menuItems = getMenuItems();
    const currentItem = menuItems.find(
      (item) => item.path === location.pathname
    );
    return currentItem?.title || "Dashboard";
  };

  const menuItems = getMenuItems();

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <Sidebar collapsible="icon" className="border-r border-gray-200">
          <SidebarHeader className="border-b border-gray-200 p-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                {isSuperAdmin && (
                  <Badge className="bg-blue-100 text-blue-800 text-xs hidden">
                    <Shield className="w-3 h-3 mr-1" />
                    Admin
                  </Badge>
                )}
              </div>
            </div>
          </SidebarHeader>

          <SidebarContent className="p-2">
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    size="lg"
                    onClick={() => navigate(item.path)}
                    isActive={location.pathname === item.path}
                    className="h-auto py-3 items-start data-[active=true]:bg-green-50 data-[active=true]:border-l-4 data-[active=true]:border-green-600 data-[active=true]:text-green-700 hover:bg-gray-50 transition-all duration-200"
                  >
                    <item.icon
                      className={`!w-6 !h-6 mt-0.5 transition-colors duration-200 ${
                        location.pathname === item.path
                          ? "text-green-600"
                          : "text-gray-600"
                      }`}
                    />
                    <div className="group-data-[collapsible=icon]:hidden flex flex-col gap-0.5 leading-tight">
                      <p
                        className={`font-medium transition-colors duration-200 ${
                          location.pathname === item.path
                            ? "text-green-700"
                            : "text-gray-900"
                        }`}
                      >
                        {item.title}
                      </p>
                      <p
                        className={`text-xs whitespace-normal leading-4 transition-colors duration-200 ${
                          location.pathname === item.path
                            ? "text-green-600"
                            : "text-gray-500"
                        }`}
                      >
                        {item.description}
                      </p>
                    </div>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarContent>
        </Sidebar>

        <SidebarInset className="flex-1 md:ml-0">
          {/* Top Header */}
          <div className="border-b border-gray-200 bg-white sticky top-0 z-10">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center space-x-4">
                <SidebarTrigger className="p-2 hover:bg-gray-100 rounded-lg" />
                <div className="hidden md:block">
                  <h1 className="font-semibold text-gray-900">
                    {getPageTitle()}
                  </h1>
                  {isSuperAdmin && (
                    <p className="text-xs text-blue-600">
                      Administration Portal
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-4">
                {/* Notifications */}
                <CustomDropdown
                  trigger={
                    <div className="relative">
                      <Button variant="ghost" size="sm" className="p-2">
                        <Bell className="!w-6 !h-6 text-gray-600" />
                      </Button>
                      {(notificationList.filter(n => !n.read).length > 0) && (
                        <Badge className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center p-0">
                          {notificationList.filter(n => !n.read).length}
                        </Badge>
                      )}
                    </div>
                  }
                  align="end"
                  side="bottom"
                  className="w-96 bg-white max-h-[500px] overflow-hidden"
                >
                  <DropdownContent className="max-h-[500px] overflow-y-auto">
                    <div className="px-4 py-4 border-b border-gray-200 flex items-center justify-between sticky -top-1 bg-white z-10">
                      <h3 className="font-semibold text-gray-900">
                        Notifications {notificationList.filter(n => !n.read).length > 0 ? `${notificationList.filter(n => !n.read).length} unread` : ""}
                      </h3>
                      <div className="flex gap-2">
                        {/* <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            console.log('Manual notification refresh requested');
                            fetchNotifications();
                          }}
                          className="text-xs text-gray-600 hover:text-gray-700 h-auto p-1"
                          title="Refresh notifications"
                        >
                          🔄
                        </Button> */}
                        {notificationList.some(n => !n.read) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleMarkAllAsRead}
                            className="text-xs text-blue-600 hover:text-blue-700 h-auto p-1"
                          >
                            Mark all read
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {isLoadingNotifications ? (
                      <DropdownItem className="justify-center py-4">
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-green-500 border-t-transparent mr-3"></div>
                          Loading notifications...
                        </div>
                      </DropdownItem>
                    ) : notificationList.length > 0 ? (
                      notificationList.map((notification) => (
                        <DropdownItem
                          key={notification.id}
                          className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                            !notification.read ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          {/* Sender Avatar */}
                          <div className="flex-shrink-0">
                            {notification.sender?.imageURL ? (
                              <img
                                src={notification.sender.imageURL}
                                alt={notification.sender.name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                                {notification.sender?.name?.charAt(0) || notification.type.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>

                          {/* Notification Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <p className="font-semibold text-sm text-gray-900 truncate">
                                {notification.title}
                              </p>
                              {!notification.read && (
                                <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1"></div>
                              )}
                            </div>
                            
                            <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                              {notification.message}
                            </p>
                            
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <span className="capitalize">{notification.type}</span>
                              <span>•</span>
                              <span>
                                {new Date(notification.timestamp).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </span>
                              {notification.priority === 'urgent' && (
                                <>
                                  <span>•</span>
                                  <Badge variant="destructive" className="text-xs px-1 py-0 h-4">
                                    Urgent
                                  </Badge>
                                </>
                              )}
                              {notification.priority === 'high' && (
                                <>
                                  <span>•</span>
                                  <Badge className="text-xs px-1 py-0 h-4 bg-orange-500">
                                    High
                                  </Badge>
                                </>
                              )}
                            </div>
                          </div>
                        </DropdownItem>
                      ))
                    ) : (
                      <DropdownItem className="justify-center text-gray-500 text-sm py-8 flex-col gap-2">
                        <Bell className="w-8 h-8 text-gray-300" />
                        <span>No notifications yet</span>
                      </DropdownItem>
                    )}
                  </DropdownContent>
                </CustomDropdown>
                {/* User Menu */}
                <CustomDropdown
                  trigger={
                    <Button
                      variant="ghost"
                      className="flex items-center bg-white space-x-2 p-2 hover:bg-gray-100"
                    >
                      {user?.imageURL ? (
                        <Avatar className="w-8 h-8">
                          <AvatarFallback
                            className={"bg-blue-100 text-blue-700"}
                          >
                            <img src={user?.imageURL} alt="profile picture" />
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        <Avatar className="w-8 h-8">
                          <AvatarFallback
                            className={
                              isSuperAdmin
                                ? "bg-blue-100 text-blue-700"
                                : "bg-green-100 text-green-700"
                            }
                          >
                            {user?.firstName.charAt(0)}
                            {user?.lastName.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <div className="hidden md:block text-left">
                        <p className="text-sm font-medium text-gray-900">
                          {user?.professionalTitle
                            ? `${user.professionalTitle}. `
                            : ""}
                          {user?.firstName} {user?.lastName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {isSuperAdmin
                            ? "System Administrator"
                            : user?.pharmacyName}
                        </p>
                      </div>
                    </Button>
                  }
                  align="end"
                  side="bottom"
                  className="w-56"
                >
                  <DropdownContent>
                    <DropdownItem onClick={handleProfileSettingsClick}>
                      <User className="w-4 h-4 mr-2" />
                      Profile Settings
                    </DropdownItem>
                    {/* <DropdownItem onClick={handleProfileSettingsClick}>
                      <Settings className="w-4 h-4 mr-2" />
                      Account Settings
                    </DropdownItem> */}
                    <DropdownSeparator />
                    <DropdownItem onClick={handleLogout} variant="destructive">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownItem>
                  </DropdownContent>
                </CustomDropdown>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-6 bg-gray-50 min-h-[calc(100vh-73px)]">
            <Outlet />
          </div>
        </SidebarInset>
      </div>
      {/* <SocketDiagnostics /> */}
    </SidebarProvider>
  );
}
